import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import deleteIcon from 'four-nets/icons/svg/icon_delete 2.svg';
import BrandAutocomplete from './brand-autocomplete';
import { Site } from 'four-nets/site';
import Spinner from 'four-nets/ui/spinner/spinner';

import defaultStyles from './brand-input.scss';

export interface Style {
    input?: string;
}

export interface Brand {
    name: string;
    id: string | null;
}

export interface Props {
    styles: typeof defaultStyles;
    style: Style;
    onChange: (brand: Brand | null) => void;
    brand: Brand | null;
}

interface State {
    value: string;
    searchValue: string;
    selectedBrand: Brand | null;
}

class BrandInput extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    ref: React.RefObject<HTMLDivElement> = React.createRef();
    setSearchValue = debounce((searchValue: string) => this.setState({ searchValue }), 300);

    constructor(props: Props) {
        super(props);
        this.state = { value: '', searchValue: '', selectedBrand: props.brand || null };
    }

    focus = () => {
        if (this.ref && this.ref.current) {
            this.ref.current.focus();
        }
    };

    onType = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.persist();
        const value = event.target.value.toLowerCase();
        const trimmedValue = value.trim();
        this.setState({ value: value }, () => this.props.onChange({ name: trimmedValue, id: null }));
        this.setSearchValue(trimmedValue);
    };

    onBrandSelect = (brand: Brand) => this.setState({ selectedBrand: brand }, () => this.props.onChange(brand));

    render() {
        const { styles, style = {} as Style } = this.props;
        return (
            <BrandAutocomplete
                onChange={this.onBrandSelect}
                value={this.state.value}
                searchValue={this.state.searchValue}
            >
                {(onKeyDown, loading) => (
                    <div
                        className={classnames(styles.inputWrapper, style.input, {
                            [styles.focused]: this.state.value,
                        })}
                        onClick={this.focus}
                    >
                        {this.state.selectedBrand ? (
                            <div className={styles.user}>
                                {this.state.selectedBrand.name}
                                <button
                                    className={styles.deleteButton}
                                    type='button'
                                    onClick={() => {
                                        this.setState({ selectedBrand: null, value: '', searchValue: '' }, () =>
                                            this.props.onChange(null)
                                        );
                                    }}
                                >
                                    <SVGIcon style={{ image: styles.deleteIcon }} src={deleteIcon} />
                                </button>
                            </div>
                        ) : (
                            <FormattedMessage id='ENTER_RECOMMENDATION_BRAND'>
                                {msg => (
                                    <input
                                        value={this.state.value}
                                        placeholder={msg as string}
                                        className={styles.brandsInput}
                                        onChange={this.onType}
                                        onKeyDown={event => {
                                            event.persist();
                                            onKeyDown(event);
                                        }}
                                        ref={this.ref as any}
                                    />
                                )}
                            </FormattedMessage>
                        )}
                        {loading ? <Spinner style={{ spinner: styles.spinner }} mini={true} /> : null}
                    </div>
                )}
            </BrandAutocomplete>
        );
    }
}

export default Styled(defaultStyles)(BrandInput);
